import { useEffect, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSun, Wind, MapPin, LocateFixed } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const AUCKLAND = { lat: -36.85, lon: 174.76, label: "Auckland (default)" };

export const uvLevel = (uv) => {
  if (uv == null) return { label: "—", color: "#9ba69f" };
  if (uv < 3) return { label: "Low", color: "#3d8b5a" };
  if (uv < 6) return { label: "Moderate", color: "#c5a23b" };
  if (uv < 8) return { label: "High", color: "#d97706" };
  if (uv < 11) return { label: "Very high", color: "#c2410c" };
  return { label: "Extreme", color: "#7c2d92" };
};

const WxIcon = ({ code, size = 16 }) => {
  if (code == null) return <Cloud size={size} />;
  if (code === 0) return <Sun size={size} />;
  if (code <= 3) return <CloudSun size={size} />;
  if (code >= 51) return <CloudRain size={size} />;
  return <Cloud size={size} />;
};

const scoreColor = (s) => (s >= 8 ? "#2f7a4a" : s >= 6 ? "#3d644e" : s >= 4 ? "#c5903b" : "#9a5b3a");

function BeachDay({ b }) {
  return (
    <div className="mt-5 border-t border-[#e2dcd4] pt-5" data-testid="beach-day-score">
      <div className="flex items-center justify-between">
        <p className="font-mono-k text-[10px] uppercase tracking-widest text-[#5a665e]">Beach day?</p>
        <p className="font-display text-2xl" style={{ color: scoreColor(b.score) }} data-testid="beach-day-value">
          {b.score}<span className="text-sm text-[#5a665e]">/10</span>
        </p>
      </div>
      <div className="mt-2 h-1 bg-[#f4f0ea]">
        <div className="h-1 transition-[width] duration-700" style={{ width: `${b.score * 10}%`, background: scoreColor(b.score) }} />
      </div>
      <p className="mt-3 text-sm text-[#1a261f]" data-testid="beach-day-label">{b.label}</p>
      <ul className="mt-1.5 space-y-0.5">
        {b.reasons.map((r) => <li key={r} className="text-xs text-[#5a665e]">· {r}</li>)}
      </ul>
    </div>
  );
}

function useWeather(at) {
  const { geo, consented } = useSite();
  const [pos, setPos] = useState(at || null);
  const [wx, setWx] = useState(null);

  useEffect(() => {
    if (at) {
      setWx(null);
      setPos(at);
    }
  }, [at]);

  useEffect(() => {
    if (at || !consented || !geo) return;
    const fallback = geo.lat != null ? { lat: geo.lat, lon: geo.lon, label: geo.city || geo.country_name || "Approximate" } : AUCKLAND;
    if (!navigator.geolocation) {
      setPos(fallback);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setPos({ lat: p.coords.latitude, lon: p.coords.longitude, label: "Your location" }),
      () => setPos(fallback),
      { timeout: 6000, maximumAge: 1800000 }
    );
  }, [consented, geo, at]);

  useEffect(() => {
    if (!pos) return;
    api.get("/weather", { params: { lat: pos.lat, lon: pos.lon } }).then(({ data }) => setWx(data)).catch(() => setWx({ error: true }));
  }, [pos]);

  return { pos, wx };
}

export default function WeatherWidget({ compact = false, at = null }) {
  const { pos, wx } = useWeather(at);
  const uv = uvLevel(wx?.uv_index);
  const uvMax = uvLevel(wx?.uv_max);

  if (compact) {
    if (!wx || wx.error) return null;
    return (
      <div className="hidden sm:flex items-center gap-3 border border-[#e2dcd4] bg-white/70 px-3 py-1.5 font-mono-k text-[11px] text-[#1a261f]" data-testid="weather-uv-badge">
        <WxIcon code={wx.weather_code} size={14} />
        <span data-testid="weather-badge-temp">{Math.round(wx.temperature)}°</span>
        <span className="w-px h-3 bg-[#e2dcd4]" />
        <span className="flex items-center gap-1.5" data-testid="weather-badge-uv">
          <span className="w-2 h-2 rounded-full" style={{ background: uv.color }} />UV {Math.round(wx.uv_index ?? 0)}
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#e2dcd4] p-6 sm:p-7" data-testid="weather-uv-widget">
      <div className="flex items-center justify-between gap-3">
        <p className="eyebrow shrink-0">Conditions now</p>
        <span className="flex items-center gap-1 font-mono-k text-[10px] text-[#5a665e] truncate min-w-0" data-testid="weather-location-label">
          {pos?.label === "Your location" ? <LocateFixed size={11} /> : <MapPin size={11} />} {pos?.label || "Locating…"}
        </span>
      </div>
      {!wx ? (
        <p className="mt-6 text-sm text-[#5a665e]" data-testid="weather-loading">Reading the sky…</p>
      ) : wx.error ? (
        <p className="mt-6 text-sm text-[#5a665e]" data-testid="weather-error">Weather is unavailable right now. Check MetService before heading out.</p>
      ) : (
        <>
          <div className="mt-5 flex items-end gap-4">
            <span className="font-display text-5xl text-[#1f402b]" data-testid="weather-temp">{Math.round(wx.temperature)}°</span>
            <span className="pb-2 text-[#5a665e] flex items-center gap-2 text-sm">
              <WxIcon code={wx.weather_code} /> <Wind size={14} /> {Math.round(wx.wind_speed)} km/h
            </span>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#e2dcd4] pt-5">
            <div data-testid="uv-current">
              <p className="font-mono-k text-[10px] uppercase tracking-widest text-[#5a665e]">UV now</p>
              <p className="mt-1 text-lg" style={{ color: uv.color }}>{Math.round(wx.uv_index ?? 0)} · {uv.label}</p>
            </div>
            <div data-testid="uv-max">
              <p className="font-mono-k text-[10px] uppercase tracking-widest text-[#5a665e]">Today's peak</p>
              <p className="mt-1 text-lg" style={{ color: uvMax.color }}>{Math.round(wx.uv_max ?? 0)} · {uvMax.label}</p>
            </div>
          </div>
          {wx.beach_day && <BeachDay b={wx.beach_day} />}
          {(wx.uv_max ?? 0) >= 3 && (
            <p className="mt-5 text-xs text-[#5a665e] leading-relaxed" data-testid="uv-advice">
              Sun protection needed today. SPF 50, shade between 11am–4pm, and reapply often.
            </p>
          )}
        </>
      )}
    </div>
  );
}
